import { useState, useCallback } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useSnackbar } from "notistack";

import {
  useLazyLookupAccountQuery,
  useChangeEmailMutation,
  type AccountLookupResult,
} from "../services/api";
import { AccountLookup } from "../components/AccountLookup";

function AccountsPage() {
  const [account, setAccount] = useState<AccountLookupResult | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [comment, setComment] = useState("");
  const [notify, setNotify] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const [lookupAccount] = useLazyLookupAccountQuery();
  const [changeEmail, { isLoading: isChanging }] = useChangeEmailMutation();

  const handleChangeEmail = useCallback(async () => {
    try {
      const result = await changeEmail({
        oldEmail: account!.email,
        newEmail,
        comment: comment || undefined,
        notify,
      }).unwrap();
      enqueueSnackbar(result.message, { variant: "success" });
      setNewEmail("");
      setComment("");
      // Re-lookup with the new email — the AccountLookup component will
      // notify us via onAccountChange once data arrives.
      await lookupAccount(newEmail).unwrap();
    } catch (err) {
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status: number }).status
          : 0;
      if (status === 409) {
        enqueueSnackbar("An account with that email already exists.", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Failed to change email.", { variant: "error" });
      }
    }
  }, [
    account,
    newEmail,
    comment,
    notify,
    changeEmail,
    lookupAccount,
    enqueueSnackbar,
  ]);

  return (
    <Stack spacing={2}>
      <AccountLookup onAccountChange={setAccount} />

      {account && (
        <>
          <Divider />
          <Typography variant="h5">Change Email</Typography>
          <TextField
            label="New Email"
            type="email"
            placeholder="Enter new email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            fullWidth
          />
          <TextField
            label="Reason (optional)"
            placeholder="e.g. domain migration"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
            }
            label="Notify customer (sends confirmation to new email, CC old email)"
          />
          <Button
            variant="contained"
            color="warning"
            onClick={handleChangeEmail}
            disabled={isChanging || !newEmail}
          >
            {isChanging ? "Changing..." : "Change Email"}
          </Button>
        </>
      )}
    </Stack>
  );
}

export { AccountsPage };
